/**
 * Asks API - Legal ask/request management and evidence linking.
 */

import apiClient, { type RequestConfig } from './client'

export interface Ask {
  id: string
  matter_id: string
  type: string
  status: string
  title: string
  body: string
  source_label: string | null
  constraints: Record<string, unknown> | null
  search_recipe: Record<string, unknown> | null
  evidence_count: number
  support_count: number
  contradict_count: number
  gap_count: number
  imported_from: string | null
  created_at: string
  updated_at: string
}

export interface AskEvidence {
  id: string
  ask_id: string
  document_id: string
  relation: string
  verification: string
  confidence: number | null
  excerpt: string | null
  excerpt_offset: number | null
  reasoning: string | null
  created_at: string
  updated_at: string
}

export interface ImportResult {
  asks: Ask[]
  count: number
}

export interface SendToReviewResult {
  queue_id: string | null
  assignments_created: number
}

export interface WorkProduct {
  id: string
  ask_id: string | null
  matter_id: string | null
  type: string
  title: string
  body: string
  content?: unknown
  citations: Record<string, unknown>[] | null
  version: number
  parent_id?: string | null
  metadata?: Record<string, unknown> | null
  created_at: string
}

export interface SearchResult {
  created: number
  updated: number
  total_evidence: number
}

export const asksApi = {
  async list(
    matterId: string,
    params?: { type?: string; status?: string },
    config?: RequestConfig
  ): Promise<Ask[]> {
    const { data } = await apiClient.get<Ask[]>(
      `/matters/${matterId}/asks`,
      { ...config, params: params as Record<string, string | number | boolean | undefined> }
    )
    return data
  },

  async get(
    matterId: string,
    askId: string,
    config?: RequestConfig
  ): Promise<Ask> {
    const { data } = await apiClient.get<Ask>(
      `/matters/${matterId}/asks/${askId}`,
      config
    )
    return data
  },

  async create(
    matterId: string,
    payload: {
      type: string
      title: string
      body?: string
      source_label?: string | null
      constraints?: Record<string, unknown> | null
      imported_from?: string | null
    },
    config?: RequestConfig
  ): Promise<Ask> {
    const { data } = await apiClient.post<Ask>(
      `/matters/${matterId}/asks`,
      payload,
      config
    )
    return data
  },

  async update(
    matterId: string,
    askId: string,
    payload: Partial<Pick<Ask, 'title' | 'body' | 'status' | 'constraints'>>,
    config?: RequestConfig
  ): Promise<Ask> {
    const { data } = await apiClient.patch<Ask>(
      `/matters/${matterId}/asks/${askId}`,
      payload,
      config
    )
    return data
  },

  async archive(
    matterId: string,
    askId: string,
    config?: RequestConfig
  ): Promise<void> {
    await apiClient.delete(`/matters/${matterId}/asks/${askId}`, config)
  },

  async importRequests(
    matterId: string,
    payload: { text: string; source_label?: string },
    config?: RequestConfig
  ): Promise<ImportResult> {
    const { data } = await apiClient.post<ImportResult>(
      `/matters/${matterId}/asks/import`,
      payload,
      config
    )
    return data
  },

  async executeSearch(
    matterId: string,
    askId: string,
    payload?: { top_k?: number; mode?: string; classify_results?: boolean },
    config?: RequestConfig
  ): Promise<SearchResult> {
    const { data } = await apiClient.post<SearchResult>(
      `/matters/${matterId}/asks/${askId}/search`,
      payload || {},
      { ...config, timeout: 2 * 60 * 1000 }  // 2 min timeout for search + classification
    )
    return data
  },

  async listEvidence(
    matterId: string,
    askId: string,
    params?: { relation?: string },
    config?: RequestConfig
  ): Promise<AskEvidence[]> {
    const { data } = await apiClient.get<AskEvidence[]>(
      `/matters/${matterId}/asks/${askId}/evidence`,
      { ...config, params: params as Record<string, string | number | boolean | undefined> }
    )
    return data
  },

  async verifyEvidence(
    matterId: string,
    askId: string,
    evidenceId: string,
    payload: { verification: string },
    config?: RequestConfig
  ): Promise<AskEvidence> {
    const { data } = await apiClient.patch<AskEvidence>(
      `/matters/${matterId}/asks/${askId}/evidence/${evidenceId}`,
      payload,
      config
    )
    return data
  },

  async sendToReview(
    matterId: string,
    askId: string,
    config?: RequestConfig
  ): Promise<SendToReviewResult> {
    const { data } = await apiClient.post<SendToReviewResult>(
      `/matters/${matterId}/asks/${askId}/send-to-review`,
      {},
      config
    )
    return data
  },

  async generateMemo(
    matterId: string,
    askId: string,
    config?: RequestConfig
  ): Promise<WorkProduct> {
    const { data } = await apiClient.post<WorkProduct>(
      `/matters/${matterId}/asks/${askId}/generate-memo`,
      {},
      config
    )
    return data
  },

  async importAllegations(
    matterId: string,
    payload: { text: string; source_label?: string },
    config?: RequestConfig
  ): Promise<ImportResult> {
    const { data } = await apiClient.post<ImportResult>(
      `/matters/${matterId}/asks/import-allegations`,
      payload,
      config
    )
    return data
  },

  async searchEstimate(
    matterId: string,
    payload: {
      ask_ids?: string[] | null
      ask_type?: string | null
      top_k?: number
      classify_results?: boolean
    },
    config?: RequestConfig
  ): Promise<BatchSearchEstimate> {
    const { data } = await apiClient.post<BatchSearchEstimate>(
      `/matters/${matterId}/asks/search-estimate`,
      payload,
      config
    )
    return data
  },

  async searchAll(
    matterId: string,
    payload: {
      ask_ids?: string[] | null
      ask_type?: string | null
      top_k?: number
      classify_results?: boolean
      mode?: string
      confirmed: boolean
    },
    config?: RequestConfig
  ): Promise<BatchSearchResult> {
    const { data } = await apiClient.post<BatchSearchResult>(
      `/matters/${matterId}/asks/search-all`,
      payload,
      { ...config, timeout: 10 * 60 * 1000 }  // 10 min timeout for batch
    )
    return data
  },

  async getPulse(
    matterId: string,
    config?: RequestConfig
  ): Promise<CasePulseResponse> {
    const { data } = await apiClient.get<CasePulseResponse>(
      `/matters/${matterId}/asks/pulse`,
      config
    )
    return data
  },

  async adversarySearch(
    matterId: string,
    askId: string,
    config?: RequestConfig
  ): Promise<AdversarySearchResponse> {
    const { data } = await apiClient.post<AdversarySearchResponse>(
      `/matters/${matterId}/asks/${askId}/adversary-search`,
      {},
      { ...config, timeout: 2 * 60 * 1000 }
    )
    return data
  },

  async getConstellation(
    matterId: string,
    config?: RequestConfig
  ): Promise<ConstellationResponse> {
    const { data } = await apiClient.get<ConstellationResponse>(
      `/matters/${matterId}/asks/constellation`,
      config
    )
    return data
  },

  async gapAnalysis(
    matterId: string,
    askId: string,
    config?: RequestConfig
  ): Promise<GapAnalysisResponse> {
    const { data } = await apiClient.post<GapAnalysisResponse>(
      `/matters/${matterId}/asks/${askId}/gap-analysis`,
      {},
      { ...config, timeout: 2 * 60 * 1000 }
    )
    return data
  },

  async analyzeTheory(
    matterId: string,
    theoryText: string,
    config?: RequestConfig
  ): Promise<TheoryAnalysisResponse> {
    const { data } = await apiClient.post<TheoryAnalysisResponse>(
      `/matters/${matterId}/asks/analyze-theory`,
      { theory_text: theoryText },
      { ...config, timeout: 3 * 60 * 1000 }
    )
    return data
  },

  async depositionPrep(
    matterId: string,
    askId: string,
    custodian: string,
    config?: RequestConfig
  ): Promise<DepositionPrepResponse> {
    const { data } = await apiClient.post<DepositionPrepResponse>(
      `/matters/${matterId}/asks/${askId}/deposition-prep`,
      { custodian },
      { ...config, timeout: 2 * 60 * 1000 }
    )
    return data
  },

  async smartImport(
    matterId: string,
    text: string,
    documentType?: string,
    config?: RequestConfig
  ): Promise<SmartImportResponse> {
    const { data } = await apiClient.post<SmartImportResponse>(
      `/matters/${matterId}/asks/smart-import`,
      { text, document_type: documentType || 'auto' },
      { ...config, timeout: 2 * 60 * 1000 }
    )
    return data
  },

  async listClaims(
    matterId: string,
    params?: {
      askId?: string | null
      workflowRunId?: string | null
      sourceType?: string | null
      status?: string | null
    },
    config?: RequestConfig
  ): Promise<TrustClaim[]> {
    const { data } = await apiClient.get<TrustClaim[]>(
      `/matters/${matterId}/claims`,
      {
        ...config,
        params: {
          ask_id: params?.askId ?? undefined,
          workflow_run_id: params?.workflowRunId ?? undefined,
          source_type: params?.sourceType ?? undefined,
          status: params?.status ?? undefined,
        },
      }
    )
    return data
  },

  async approveClaim(
    matterId: string,
    claimId: string,
    payload?: { factText?: string | null },
    config?: RequestConfig
  ): Promise<ApprovedFact> {
    const { data } = await apiClient.post<ApprovedFact>(
      `/matters/${matterId}/claims/${claimId}/approve`,
      { fact_text: payload?.factText ?? null },
      config
    )
    return data
  },

  async bulkApproveClaims(
    matterId: string,
    payload: {
      claimIds: string[]
      factOverrides?: Record<string, string> | null
    },
    config?: RequestConfig
  ): Promise<ApprovedFact[]> {
    const { data } = await apiClient.post<ApprovedFact[]>(
      `/matters/${matterId}/claims/bulk-approve`,
      {
        claim_ids: payload.claimIds,
        fact_overrides: payload.factOverrides ?? null,
      },
      config
    )
    return data
  },

  async rejectClaim(
    matterId: string,
    claimId: string,
    config?: RequestConfig
  ): Promise<ClaimActionResult> {
    const { data } = await apiClient.post<ClaimActionResult>(
      `/matters/${matterId}/claims/${claimId}/reject`,
      {},
      config
    )
    return data
  },

  async resolveClaim(
    matterId: string,
    claimId: string,
    config?: RequestConfig
  ): Promise<ClaimActionResult> {
    const { data } = await apiClient.post<ClaimActionResult>(
      `/matters/${matterId}/claims/${claimId}/resolve`,
      {},
      config
    )
    return data
  },

  async ignoreClaim(
    matterId: string,
    claimId: string,
    config?: RequestConfig
  ): Promise<ClaimActionResult> {
    const { data } = await apiClient.post<ClaimActionResult>(
      `/matters/${matterId}/claims/${claimId}/ignore`,
      {},
      config
    )
    return data
  },

  async convertClaimToAsk(
    matterId: string,
    claimId: string,
    config?: RequestConfig
  ): Promise<Ask> {
    const { data } = await apiClient.post<Ask>(
      `/matters/${matterId}/claims/${claimId}/convert-to-ask`,
      {},
      config
    )
    return data
  },

  async listApprovedFacts(
    matterId: string,
    params?: { askId?: string | null; status?: string | null },
    config?: RequestConfig
  ): Promise<ApprovedFact[]> {
    const { data } = await apiClient.get<ApprovedFact[]>(
      `/matters/${matterId}/approved-facts`,
      {
        ...config,
        params: {
          ask_id: params?.askId ?? undefined,
          status: params?.status ?? undefined,
        },
      }
    )
    return data
  },

  async buildPacket(
    matterId: string,
    payload: { packetType: string; askId?: string | null },
    config?: RequestConfig
  ): Promise<PacketBuildResponse> {
    const { data } = await apiClient.post<PacketBuildResponse>(
      `/matters/${matterId}/packets/build`,
      {
        packet_type: payload.packetType,
        ask_id: payload.askId ?? null,
      },
      config
    )
    return data
  },

  async listPacketSections(
    matterId: string,
    packetType: string,
    config?: RequestConfig
  ): Promise<PacketSection[]> {
    const { data } = await apiClient.get<PacketSection[]>(
      `/matters/${matterId}/packets/sections`,
      {
        ...config,
        params: { packet_type: packetType },
      }
    )
    return data
  },
}

export interface BatchSearchEstimate {
  ask_count: number
  allegation_count: number
  request_count: number
  docs_in_matter: number
  docs_per_ask: number
  total_classification_calls: number
  classify_enabled: boolean
  estimated_cost_usd: number
  cost_range_low_usd: number
  cost_range_high_usd: number
  budget: {
    allowed: boolean
    daily_spend_usd: number
    daily_limit_usd: number
    monthly_spend_usd: number
    monthly_limit_usd: number
    reason: string | null
  }
}

export interface BatchSearchResult {
  completed: number
  failed: number
  total_evidence: number
  failures: { ask_id: string; title: string; error: string }[]
}

export interface AskPulseScore {
  ask_id: string
  title: string
  type: string
  status: string
  score: number
  evidence_count: number
  support_count: number
  contradict_count: number
  gap_count: number
  verified_count: number
  total_count: number
  verdict: 'strong' | 'moderate' | 'weak' | 'empty' | 'at_risk'
}

export interface CasePulseResponse {
  matter_id: string
  overall_score: number
  overall_verdict: string
  summary: string
  asks: AskPulseScore[]
  zero_evidence_count: number
  at_risk_count: number
}

export interface AdversarialHit {
  document_id: string
  filename: string
  threat_level: 'high' | 'medium' | 'low' | 'none'
  attack_vector: string
  excerpt: string
  reasoning: string
  confidence: number
}

export interface AdversarySearchResponse {
  ask_id: string
  ask_title: string
  hits: AdversarialHit[]
  high_threat_count: number
  medium_threat_count: number
  total_analyzed: number
  workflow_run_id?: string | null
}

export interface GraphNode {
  id: string
  label: string
  type: 'ask' | 'document'
  ask_type?: string | null
  connections: number
}

export interface GraphEdge {
  source: string
  target: string
  relation: string
  confidence: number
}

export interface ConstellationResponse {
  nodes: GraphNode[]
  edges: GraphEdge[]
  ask_count: number
  doc_count: number
  key_documents: string[]
}

export interface EvidenceGap {
  gap_id?: string | null
  category: 'document_type' | 'date_range' | 'custodian' | 'topic'
  description: string
  severity: 'critical' | 'important' | 'minor'
  suggested_query: string
}

export interface GapAnalysisResponse {
  ask_id: string
  ask_title: string
  completeness_score: number
  gaps: EvidenceGap[]
  evidence_summary: string
  workflow_run_id?: string | null
}

export interface ClaimEvidence {
  document_id: string
  filename: string
  excerpt: string
  relation: 'supporting' | 'contradicting'
}

export interface ClaimAnalysis {
  claim_id?: string | null
  claim_text: string
  claim_index: number
  status: 'supported' | 'contradicted' | 'unsupported' | 'mixed'
  confidence: number
  evidence: ClaimEvidence[]
}

export interface TheoryAnalysisResponse {
  claims: ClaimAnalysis[]
  overall_support: number
  supported_count: number
  contradicted_count: number
  unsupported_count: number
  workflow_run_id?: string | null
}

export interface DepositionQuestion {
  category: 'foundation' | 'key_document' | 'gap_filling'
  question: string
  source_document_id: string | null
  source_excerpt: string | null
  follow_up: string | null
}

export interface DepositionPrepResponse {
  ask_id: string
  ask_title: string
  custodian: string
  questions: DepositionQuestion[]
  foundation_count: number
  key_document_count: number
  gap_filling_count: number
  workflow_run_id?: string | null
}

export interface ExtractedAsk {
  type: string
  title: string
  body: string
  source_label: string | null
  suggested_constraints: Record<string, unknown> | null
  claim_id?: string | null
}

export interface SmartImportResponse {
  detected_type: string
  extracted_asks: ExtractedAsk[]
  total_count: number
  summary: string
  workflow_run_id?: string | null
  extraction_version?: string | null
}

export interface TrustClaimEvidenceLink {
  id: string
  document_id: string
  relation: string
  confidence: number | null
  excerpt: string | null
  excerpt_offset: number | null
  reasoning: string | null
  source: string
  verified_by: string | null
  verified_at: string | null
  created_at: string
}

export interface TrustClaim {
  id: string
  ask_id: string | null
  matter_id: string
  workflow_run_id: string | null
  source_type: string
  source_label: string | null
  claim_text: string
  claim_index: number | null
  status: string
  created_by: string | null
  created_at: string
  updated_at: string
  evidence_links: TrustClaimEvidenceLink[]
}

export interface ApprovedFact {
  id: string
  claim_id: string | null
  ask_id: string | null
  fact_text: string
  status: string
  approved_by: string | null
  approved_at: string
  citation_bundle: Record<string, unknown>
  version: number
  source_workflow_run_id: string | null
  created_at: string
}

export interface ClaimActionResult {
  claim: TrustClaim
}

export interface PacketSection {
  id: string
  packet_type: string
  section_type: string
  title: string
  body: string
  source_kind: string
  source_refs: Record<string, unknown>
  version: number
  is_current: boolean
  created_at: string
}

export interface PacketBuildResponse {
  workflow_run_id: string
  sections: PacketSection[]
}
